/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext } from 'react';
import { ReactSVG } from 'react-svg';
import styled, { ThemeContext } from 'styled-components';
import { background, border, color, layout, position, shadow, space, compose } from 'styled-system';

import { createSystemPropFilter } from '@utils/styled-component-tools.ts';

import { iconRegistry } from './iconRegistry.tsx';
import { StyledSVGBoxProps, StyledSVGProps, IconProps, RenderRegistryIconProps } from './types.ts';
import {
  getTransformCSS,
  getCommonCSS,
  normalizeDimension,
  calculateContainerDimensions,
  configureSvg,
  getIconA11y,
} from './utils.ts';

const system = compose(background, border, color, layout, position, shadow, space);

// Styled container for URL-based icons
const StyledSVGBox = styled.span.withConfig({
  shouldForwardProp: createSystemPropFilter(system),
})<StyledSVGBoxProps>`
  ${getTransformCSS}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  width: ${({ width }) => normalizeDimension(width)};
  height: ${({ height }) => normalizeDimension(height)};
  ${getCommonCSS}
  ${system}

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Styled SVG for registry-based icons
const StyledSVG = styled.svg.withConfig({
  // width/height are also set deliberately as SVG attributes in renderRegistryIcon
  shouldForwardProp: createSystemPropFilter(system, ['width', 'height']),
})<StyledSVGProps>`
  ${getTransformCSS}
  ${getCommonCSS}
  ${system}
`;

const isRegistryIconName = (name: string): name is keyof typeof iconRegistry => Object.hasOwn(iconRegistry, name);

const renderRegistryIcon = ({
  name,
  wrapper,
  width,
  height,
  'data-testid': dataTestId,
  ...rest
}: RenderRegistryIconProps) => {
  const registryEntry = name && isRegistryIconName(name) ? iconRegistry[name] : undefined;
  if (!registryEntry) {
    return null;
  }

  const svgElement = (
    <StyledSVG
      data-testid={dataTestId}
      width={width}
      height={height}
      name={name}
      fill="currentColor"
      strokeWidth="0"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {registryEntry}
    </StyledSVG>
  );

  // Wrap SVG if wrapper prop is provided
  if (wrapper) {
    const WrapperElement = wrapper;
    return <WrapperElement style={{ width, height }}>{svgElement}</WrapperElement>;
  }

  return svgElement;
};

const IconBase = ({ ref, ...props }: IconProps) => {
  const theme = useContext(ThemeContext) as Record<string, any>;
  const cdnUrl = theme.getBaseUIUrl?.() || null;

  const {
    name,
    wrapper,
    viewBox: viewBoxProp,
    focusable = false,
    role,
    title,
    'aria-label': ariaLabel,
    fillColor,
    shadowed,
    degreesOfIconRotation,
    flipOnXAxis,
    // These props are used by calculateContainerDimensions below and must not reach the SVG DOM.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    iconSize,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    size,
    cdn = false,
    fallback,
    preserveArtworkColors = false,
    'data-testid': dataTestId = 'Icon',
    ...rest
  } = props;

  // Calculate dimensions using utility
  const dimensions = calculateContainerDimensions({ ...props, theme });
  const isRegistryIcon = name && isRegistryIconName(name);

  let viewBox = viewBoxProp;
  if (!viewBox && isRegistryIcon) {
    viewBox = '0 0 32 32';
  }

  const isIconColored = preserveArtworkColors || name?.includes?.('.color');
  const src = cdn && name && cdnUrl ? `${cdnUrl}${name}.svg` : null;
  const a11yProps = getIconA11y({ title, ariaLabel, role });
  const commonProps = {
    ref,
    $fillColor: fillColor,
    $shadowed: shadowed,
    $degreesOfIconRotation: degreesOfIconRotation,
    $flipOnXAxis: flipOnXAxis,
  };
  const registryRenderProps = {
    ...commonProps,
    wrapper,
    viewBox,
    focusable,
    name,
    ...rest,
    ...a11yProps,
    ...dimensions,
    'data-testid': dataTestId,
  };
  const errorFallbackProps = {
    ...registryRenderProps,
    name: 'error',
    viewBox: '0 0 32 32',
    style: { color: 'red', ...rest.style },
  };

  // URL-based Icon (Venue-specific CDN)
  if (cdn && name && src) {
    return (
      <StyledSVGBox data-testid={dataTestId} name={name} {...dimensions} {...rest} {...commonProps}>
        <ReactSVG
          focusable={focusable || false}
          {...a11yProps}
          src={src}
          wrapper="span"
          beforeInjection={svg =>
            configureSvg(svg, {
              ...dimensions,
              viewBox: viewBoxProp,
              title: a11yProps.title,
              isIconColored,
              removeFill: !!fillColor,
              role: a11yProps.role,
            })
          }
          fallback={() =>
            isRegistryIcon
              ? renderRegistryIcon(registryRenderProps)
              : (fallback ?? renderRegistryIcon(errorFallbackProps))
          }
        />
      </StyledSVGBox>
    );
  }

  // Registry-based Icon
  if (isRegistryIcon) {
    return renderRegistryIcon(registryRenderProps);
  }

  // Error fallback
  return fallback ?? renderRegistryIcon(errorFallbackProps);
};

IconBase.displayName = 'Icon';

// Exporting as a styled component allows it to be used as a selector in other styled components
const Icon = styled(IconBase)``;

export { Icon };
export default Icon;
